import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Info } from "lucide-react";

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

export function Field({ label, value, onChange, step, prefix, suffix, testid }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#57534E]">
        {label}
      </Label>
      <div className="relative mt-1">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#57534E]">
            {prefix}
          </span>
        )}
        <Input
          type="number"
          step={step || "any"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${prefix ? "pl-7" : ""} ${suffix ? "pr-10" : ""} ${inputCls}`}
          data-testid={testid}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#57534E]">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

export function OvernightSection({ overnight, setOvernight, suggestOvernight }) {
  const nights = Math.max(1, Number(overnight.nights) || 1);
  const total = ((Number(overnight.hotelCost) || 0) + (Number(overnight.mealPerDiem) || 0)) * nights;
  return (
    <div className="space-y-3">
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox
          checked={overnight.enabled}
          onCheckedChange={(v) => setOvernight({ ...overnight, enabled: !!v })}
          className="mt-0.5 data-[state=checked]:bg-[#F97316] data-[state=checked]:border-[#F97316]"
          data-testid="overnight-checkbox"
        />
        <div>
          <span className="text-sm font-medium text-[#1C1917]">Driver needs an overnight stay</span>
          {suggestOvernight && (
            <span className="flex items-center gap-1 text-xs text-[#EA580C] mt-0.5">
              <Info className="h-3 w-3" /> Suggested — route exceeds 10 hours
            </span>
          )}
        </div>
      </label>
      {overnight.enabled && (
        <div className="pl-7 space-y-2">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Hotel $/night" value={overnight.hotelCost} onChange={(v) => setOvernight({ ...overnight, hotelCost: v })} prefix="$" testid="input-hotel" />
            <Field label="# Nights" value={overnight.nights ?? 1} onChange={(v) => setOvernight({ ...overnight, nights: v })} testid="input-nights" />
            <Field label="Meal per diem $" value={overnight.mealPerDiem} onChange={(v) => setOvernight({ ...overnight, mealPerDiem: v })} prefix="$" testid="input-perdiem" />
          </div>
          <p className="text-[11px] text-[#57534E]" data-testid="overnight-total-hint">
            Overnight total: <b className="text-[#1C1917]">${total.toFixed(2)}</b>{" "}
            (hotel + per diem × {nights} night{nights > 1 ? "s" : ""})
          </p>
        </div>
      )}
    </div>
  );
}

export function BackhaulSection({ backhaul, setBackhaul }) {
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 cursor-pointer">
        <Checkbox
          checked={backhaul.enabled}
          onCheckedChange={(v) => setBackhaul({ ...backhaul, enabled: !!v })}
          className="data-[state=checked]:bg-[#F97316] data-[state=checked]:border-[#F97316]"
          data-testid="backhaul-checkbox"
        />
        <span className="text-sm font-medium text-[#1C1917]">Picking up a paid load on the way back</span>
      </label>
      {backhaul.enabled && (
        <div className="pl-7">
          <Field label="Backhaul revenue $" value={backhaul.revenue} onChange={(v) => setBackhaul({ ...backhaul, revenue: v })} prefix="$" testid="input-backhaul" />
        </div>
      )}
    </div>
  );
}

export function BonusSection({ bonus, setBonus }) {
  const perDrop = Number(bonus.perDrop ?? bonus.flatAmount) || 0;
  const drops = Math.max(1, Number(bonus.drops) || 1);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[#1C1917]">Driver Bonus</span>
        <div className="flex items-center gap-2 text-xs">
          <span className={bonus.type === "flat" ? "font-bold text-[#F97316]" : "text-[#57534E]"}>
            Flat $
          </span>
          <Switch
            checked={bonus.type === "pct"}
            onCheckedChange={(v) => setBonus({ ...bonus, type: v ? "pct" : "flat" })}
            className="data-[state=checked]:bg-[#F97316]"
            data-testid="bonus-type-switch"
          />
          <span className={bonus.type === "pct" ? "font-bold text-[#F97316]" : "text-[#57534E]"}>
            % margin
          </span>
        </div>
      </div>
      {bonus.type === "flat" ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Bonus per drop"
              value={bonus.perDrop ?? bonus.flatAmount ?? ""}
              onChange={(v) => setBonus({ ...bonus, perDrop: v })}
              prefix="$"
              testid="input-bonus-perdrop"
            />
            <Field
              label="# of drops"
              value={bonus.drops ?? 1}
              onChange={(v) => setBonus({ ...bonus, drops: v })}
              testid="input-bonus-drops"
            />
          </div>
          <p className="text-[11px] text-[#57534E]" data-testid="bonus-total-hint">
            Total flat bonus: <b className="text-[#1C1917]">${(perDrop * drops).toFixed(2)}</b>{" "}
            (${perDrop}/drop × {drops} drop{drops > 1 ? "s" : ""})
          </p>
        </div>
      ) : (
        <Field label="% of net margin" value={bonus.pctAmount} onChange={(v) => setBonus({ ...bonus, pctAmount: v })} suffix="%" testid="input-bonus-pct" />
      )}
    </div>
  );
}
